import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CollapsedDisclaimer } from '../compliance/CollapsedDisclaimer'
import CoachSectionCard from '../ui/CoachSectionCard'
import { usePredictiveCopy } from '../../hooks/usePredictiveCopy'
import { sanitizeCoachText } from '../../lib/aiResponseValidator'
import { supabase } from '../../lib/supabaseClient'
import {
  applyCopingStyleToSignal,
  submitPredictiveFeedback,
  type StressCopingStyle,
} from '../../lib/predictiveFeedback'
import { parseStressEventContext } from '../../lib/predictiveSignals'
import StressEventPreview from './StressEventPreview'
import { Skeleton, SkeletonCard } from '../ui/Skeleton'

type RoutineOverride = {
  session_type?: string
  volume_adjustment_pct?: number
  intensity?: string
  recommended_activities?: string[]
  coach_note?: string
  intervention_mode?: string
}

type DietOverride = {
  focus?: string
  key_nutrients?: string[]
  meal_timing?: string
  items?: Array<{ name: string; quantity: string; priority: string }>
}

type CartItem = {
  product_name: string
  store: string
  price_eur: number
  quantity_label: string
  is_organic: boolean
  is_fallback: boolean
}

export type PredictiveSignal = {
  id: string
  signal_type: string
  confidence_score: number
  trigger_context: Record<string, unknown>
  suggested_routine_override: RoutineOverride | null
  suggested_diet_override: DietOverride | null
  suggested_cart: CartItem[] | null
  cart_total_eur: number | null
  cart_within_budget: boolean | null
  fallback_ingredients: string[] | null
}

const SIGNAL_COLORS: Record<string, string> = {
  overtraining: 'border-orange-500/30 bg-orange-950/20',
  recovery_debt_critical: 'border-red-500/30 bg-red-950/20',
  stress_pre_event: 'border-amber-500/30 bg-amber-950/20',
  friction_zone: 'border-kilo-line bg-kilo-card',
  nutrient_deficiency: 'border-kilo-accent/20 bg-kilo-accent/5',
}

type PredictiveSignalsPanelProps = {
  onEvaluated?: (status: string) => void
  /** Wrap signals in a collapsible Interventions section card */
  grouped?: boolean
}

function interventionForStyle(
  copingStyle: StressCopingStyle,
  fallback: 'deload' | 'maintain',
): 'deload' | 'maintain' {
  if (copingStyle === 'movement_first') return 'maintain'
  if (copingStyle === 'conserve_energy') return 'deload'
  return fallback
}

const PredictiveSignalsPanel = ({ onEvaluated, grouped = false }: PredictiveSignalsPanelProps) => {
  const { t } = useTranslation()
  const { signalLabel, stressEventCoachSummary, confidenceLearningHint } = usePredictiveCopy()
  const [signals, setSignals] = useState<PredictiveSignal[]>([])
  const [initialLoading, setInitialLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [feedbackPending, setFeedbackPending] = useState<string | null>(null)
  const [copingStyle, setCopingStyle] = useState<StressCopingStyle>('auto')
  const [feedbackError, setFeedbackError] = useState<string | null>(null)
  const [feedbackOk, setFeedbackOk] = useState<string | null>(null)

  const evaluate = useCallback(
    async (forceRecompute = false) => {
      if (forceRecompute) setRefreshing(true)
      else setInitialLoading(true)

      try {
        const { data, error } = await supabase.functions.invoke('evaluate-predictive-coaching', {
          body: forceRecompute ? { forceRecompute: true } : {},
        })

        if (error) {
          setFeedbackError(error.message)
          return
        }

        if (data?.error) {
          setFeedbackError(String(data.error))
          return
        }

        if (Array.isArray(data?.signals)) {
          setSignals(data.signals as PredictiveSignal[])
          onEvaluated?.(String(data.status ?? 'ok'))

          const stress = (data.signals as PredictiveSignal[]).find((s) => s.signal_type === 'stress_pre_event')
          const ctxStyle = stress?.trigger_context?.effective_coping_style
          if (ctxStyle === 'movement_first' || ctxStyle === 'conserve_energy' || ctxStyle === 'auto') {
            setCopingStyle(ctxStyle)
          }
        }
      } catch (err) {
        setFeedbackError(err instanceof Error ? err.message : t('predictive.refreshError'))
      } finally {
        setInitialLoading(false)
        setRefreshing(false)
      }
    },
    [onEvaluated, t],
  )

  useEffect(() => {
    void evaluate()
  }, [evaluate])

  async function resolveSignal(signalId: string): Promise<boolean> {
    const { error } = await supabase
      .from('predictive_signals')
      .update({ is_resolved: true })
      .eq('id', signalId)

    if (error) {
      setFeedbackError(error.message)
      return false
    }

    setSignals((prev) => prev.filter((s) => s.id !== signalId))
    return true
  }

  async function handleFeedback(
    signalId: string,
    feedbackType: 'helpful' | 'not_helpful' | 'movement_first' | 'conserve_energy',
  ) {
    setFeedbackError(null)
    setFeedbackOk(null)
    setFeedbackPending(`${signalId}:${feedbackType}`)

    const previousSignals = signals
    const previousStyle = copingStyle

    if (feedbackType === 'movement_first' || feedbackType === 'conserve_energy') {
      setCopingStyle(feedbackType)
      setSignals((prev) =>
        prev.map((s) =>
          s.id === signalId ? applyCopingStyleToSignal(s, feedbackType) : s,
        ),
      )
    }

    const { error } = await submitPredictiveFeedback(signalId, feedbackType)
    setFeedbackPending(null)

    if (error) {
      setSignals(previousSignals)
      setCopingStyle(previousStyle)
      setFeedbackError(error)
      return
    }

    if (feedbackType === 'movement_first') {
      setFeedbackOk(t('predictive.feedback.movementFirst'))
    } else if (feedbackType === 'conserve_energy') {
      setFeedbackOk(t('predictive.feedback.conserveEnergy'))
    } else if (feedbackType === 'helpful') {
      setFeedbackOk(t('predictive.feedback.helpful'))
    }

    await resolveSignal(signalId)
  }

  const teaser = useMemo(() => {
    if (signals.length === 0) return null
    return signalLabel(signals[0].signal_type)
  }, [signals, signalLabel])

  if (initialLoading) {
    if (grouped) {
      return (
        <div className="space-y-2" role="status">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      )
    }
    return (
      <div role="status">
        <SkeletonCard />
      </div>
    )
  }

  if (signals.length === 0) return null

  const signalList = (
    <div className="space-y-3">
      {!grouped ? (
        <p className="text-xs font-bold uppercase tracking-widest text-kilo-accent/80">{t('predictive.title')}</p>
      ) : null}
      {feedbackError ? (
        <p className="rounded-xl border border-red-900/50 bg-red-950/40 px-3 py-2 text-sm text-red-400">{feedbackError}</p>
      ) : null}
      {feedbackOk ? (
        <p className="rounded-xl border border-kilo-accent/30 bg-kilo-accent/5 px-3 py-2 text-sm text-kilo-accent">{feedbackOk}</p>
      ) : null}
      {refreshing ? <p className="text-xs text-kilo-soft">{t('predictive.updating')}</p> : null}

      {signals.map((signal) => {
        const routine = signal.suggested_routine_override
        const diet = signal.suggested_diet_override
        const colorClass = SIGNAL_COLORS[signal.signal_type] ?? SIGNAL_COLORS.friction_zone
        const stressEvent =
          signal.signal_type === 'stress_pre_event'
            ? parseStressEventContext(signal.trigger_context)
            : null

        const activeIntervention = stressEvent
          ? interventionForStyle(copingStyle, stressEvent.interventionMode)
          : 'deload'

        return (
          <div key={signal.id} className={`rounded-2xl border p-4 ${colorClass}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold text-kilo-text">
                  {signalLabel(signal.signal_type)}
                </h3>
                <p className="mt-0.5 text-xs text-kilo-muted">
                  {t('predictive.confidence', { pct: Math.round(signal.confidence_score * 100) })}
                  {stressEvent ? (
                    <span className="block text-[10px] text-kilo-soft">{confidenceLearningHint(stressEvent)}</span>
                  ) : null}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void resolveSignal(signal.id)}
                className="shrink-0 text-xs text-kilo-soft hover:text-kilo-text"
              >
                {t('predictive.dismiss')}
              </button>
            </div>

            {stressEvent ? (
              <>
                <p className="mt-2 text-sm leading-relaxed text-kilo-text">
                  {stressEventCoachSummary({
                    ...stressEvent,
                    interventionMode: activeIntervention,
                    effectiveCopingStyle: copingStyle,
                    learningApplied: copingStyle !== 'auto' || stressEvent.learningApplied,
                  })}
                </p>
                <StressEventPreview
                  context={{
                    ...stressEvent,
                    interventionMode: activeIntervention,
                    effectiveCopingStyle: copingStyle,
                    learningApplied: copingStyle !== 'auto' || stressEvent.learningApplied,
                  }}
                />
                {routine?.coach_note ? (
                  <p className="mt-3 text-sm leading-relaxed text-kilo-text">{routine.coach_note}</p>
                ) : null}
                <div className="mt-4 space-y-2">
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-kilo-soft">
                    {t('predictive.teachStyle')}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      disabled={Boolean(feedbackPending)}
                      onClick={() => void handleFeedback(signal.id, 'movement_first')}
                      className={`rounded-xl border px-3 py-2.5 text-xs font-semibold transition-colors disabled:opacity-50 ${
                        activeIntervention === 'maintain'
                          ? 'border-kilo-accent bg-kilo-accent/15 text-kilo-accent ring-1 ring-kilo-accent/40'
                          : 'border-kilo-line bg-kilo-card text-kilo-text hover:border-kilo-soft'
                      }`}
                    >
                      {feedbackPending === `${signal.id}:movement_first` ? t('predictive.saving') : t('predictive.trainingHelps')}
                    </button>
                    <button
                      type="button"
                      disabled={Boolean(feedbackPending)}
                      onClick={() => void handleFeedback(signal.id, 'conserve_energy')}
                      className={`rounded-xl border px-3 py-2.5 text-xs font-semibold transition-colors disabled:opacity-50 ${
                        activeIntervention === 'deload'
                          ? 'border-amber-400 bg-amber-500/15 text-amber-200 ring-1 ring-amber-400/40'
                          : 'border-kilo-line bg-kilo-card text-kilo-text hover:border-kilo-soft'
                      }`}
                    >
                      {feedbackPending === `${signal.id}:conserve_energy` ? t('predictive.saving') : t('predictive.protectEnergy')}
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={Boolean(feedbackPending)}
                      onClick={() => void handleFeedback(signal.id, 'helpful')}
                      className="flex-1 rounded-lg border border-kilo-line bg-kilo-card py-2 text-xs text-kilo-text disabled:opacity-50"
                    >
                      {t('predictive.thisFits')}
                    </button>
                    <button
                      type="button"
                      disabled={Boolean(feedbackPending)}
                      onClick={() => void handleFeedback(signal.id, 'not_helpful')}
                      className="flex-1 rounded-lg border border-kilo-line bg-kilo-card py-2 text-xs text-kilo-text disabled:opacity-50"
                    >
                      {t('predictive.notForMe')}
                    </button>
                  </div>
                </div>
              </>
            ) : routine?.coach_note ? (
              <p className="mt-2 text-sm leading-relaxed text-kilo-text">
                {sanitizeCoachText(routine.coach_note, signal.signal_type) ?? routine.coach_note}
              </p>
            ) : null}

            {routine?.session_type ? (
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="rounded-lg bg-kilo-nested px-2 py-1 text-xs text-kilo-text">
                  {routine.session_type.replace(/_/g, ' ')}
                </span>
                {routine.volume_adjustment_pct != null ? (
                  <span className="rounded-lg bg-kilo-nested px-2 py-1 text-xs text-kilo-text">
                    {t('predictive.volumeAdjust', { pct: `${routine.volume_adjustment_pct > 0 ? '+' : ''}${routine.volume_adjustment_pct}` })}
                  </span>
                ) : null}
              </div>
            ) : null}

            {diet?.items && diet.items.length > 0 ? (
              <div className="mt-3">
                <p className="text-xs font-medium text-kilo-muted">{t('predictive.recoveryNutrition')}</p>
                <ul className="mt-1 space-y-0.5">
                  {diet.items.slice(0, 4).map((item) => (
                    <li key={`${item.name}-${item.quantity}`} className="text-xs text-kilo-text">
                      {item.quantity} {item.name}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {signal.suggested_cart && signal.suggested_cart.length > 0 ? (
              <div className="mt-3 rounded-xl border border-kilo-border bg-kilo-nested/50 p-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-kilo-muted">{t('predictive.suggestedCart')}</p>
                  {signal.cart_total_eur != null ? (
                    <span className="text-xs text-kilo-text">
                      €{signal.cart_total_eur.toFixed(2)}
                      {signal.cart_within_budget === false ? t('predictive.overBudget') : ''}
                    </span>
                  ) : null}
                </div>
                <ul className="mt-2 space-y-1">
                  {signal.suggested_cart.slice(0, 5).map((item) => (
                    <li key={item.product_name} className="flex justify-between text-xs text-kilo-text">
                      <span>
                        {item.product_name}
                        {item.is_organic ? t('predictive.bioTag') : ''}
                        {item.is_fallback ? t('predictive.budgetPick') : ''}
                      </span>
                      <span className="text-kilo-soft">{item.store}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : signal.fallback_ingredients && signal.fallback_ingredients.length > 0 ? (
              <div className="mt-3">
                <p className="text-xs font-medium text-kilo-muted">{t('predictive.shoppingList')}</p>
                <ul className="mt-1 space-y-0.5">
                  {signal.fallback_ingredients.slice(0, 5).map((item) => (
                    <li key={item} className="text-xs text-kilo-text">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <CollapsedDisclaimer />
          </div>
        )
      })}
    </div>
  )

  if (grouped) {
    return (
      <CoachSectionCard
        title={t('coach.sections.interventions')}
        icon="update"
        badge={signals.length}
        defaultExpanded={false}
        teaser={teaser}
        featured
      >
        {signalList}
      </CoachSectionCard>
    )
  }

  return signalList
}

export default PredictiveSignalsPanel
